/**
 * Фото прямо в кадр: три входа в одну дверь — кнопка, перетаскивание файлов
 * и Ctrl-V. Вставка из буфера — главный: скриншот референса чаще всего уже в
 * буфере, и путь «сохрани на диск, потом выбери файл» здесь лишний.
 *
 * У вставки нет своего адресата, поэтому панель «взводится»: Ctrl-V попадает
 * в ту, что открыта последней (или куда последний раз роняли/выбирали файлы).
 * Иначе вставка не знала бы, в какой из пяти кадров ей целиться.
 *
 * ПЕРВЫЙ файл встаёт в выбранный слот кадра, остальные ложатся в библиотеку
 * проекта без слота — так решает сервер, и подпись это говорит.
 */

import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { Upload } from "lucide-react";
import type { DragEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { apiFetch } from "@/lib/api";
import type { Slot } from "@/lib/project";
import { errorText } from "@/lib/project";

import { Btn, Hint, selectClass } from "./ui";

type UploadResult = { added: number; errors: string[]; slot: string | null };

export function ShotUpload({
  pid,
  sid,
  freeSlots,
  allSlots,
  armed,
  onArm,
  onUploaded,
}: {
  pid: number;
  sid: number;
  freeSlots: Slot[];
  allSlots: Slot[];
  armed: boolean;
  onArm: () => void;
  onUploaded: () => void;
}) {
  const freeIds = new Set(freeSlots.map((s) => s.id));
  const options = [
    ...freeSlots.map((s) => ({ code: s.slot_code, label: `${s.slot_name} — свободен` })),
    ...allSlots.filter((s) => !freeIds.has(s.id)).map((s) => ({ code: s.slot_code, label: `${s.slot_name} — заменить` })),
  ];
  const [slot, setSlot] = useState("");
  const slotCode = options.some((o) => o.code === slot) ? slot : (options[0]?.code ?? "");
  const [status, setStatus] = useState("");
  const [isDrop, setDrop] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: (files: File[]) => {
      const body = new FormData();
      for (const file of files) body.append("files", file);
      if (slotCode) body.append("slot_code", slotCode);
      return apiFetch<UploadResult>(`/api/projects/${pid}/shots/${sid}/upload`, { method: "POST", body });
    },
    onMutate: (files) => setStatus(files.length > 1 ? `загружаю ${files.length}…` : "загружаю…"),
    onSuccess: (result) => {
      const errors = result.errors.join("; ");
      if (result.added) {
        setStatus(errors ? `загружено ${result.added}; не вошли: ${errors}` : `загружено ${result.added}`);
        onUploaded();
      } else {
        setStatus(errors || "ничего не загрузилось");
      }
    },
    onError: (error) => setStatus(errorText(error)),
  });

  const send = (files: File[]) => {
    if (!files.length || upload.isPending) return;
    upload.mutate(files);
  };

  // Ctrl-V слушает только взведённая панель — она одна на странице.
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => {
    if (!armed) return;
    const onPaste = (e: ClipboardEvent) => {
      const files = [...(e.clipboardData?.files ?? [])];
      if (!files.length) return;
      e.preventDefault();
      sendRef.current(files);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [armed]);

  const hasFiles = (e: DragEvent) => e.dataTransfer.types.includes("Files");

  return (
    <div
      data-annot="загрузка в кадр"
      onMouseDown={onArm}
      onDragOver={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setDrop(true);
      }}
      onDragLeave={() => setDrop(false)}
      onDrop={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setDrop(false);
        onArm();
        send([...e.dataTransfer.files]);
      }}
      className={clsx(
        "flex flex-wrap items-center gap-2 rounded-[var(--radius-ui)] border border-dashed p-2.5",
        isDrop ? "border-accent bg-accent-dim" : armed ? "border-accent/60 bg-bg" : "border-border-strong bg-bg",
      )}
    >
      <select
        value={slotCode}
        onChange={(e) => setSlot(e.target.value)}
        title="в какой слот кадра встанет первый файл"
        className={selectClass}
      >
        {options.map((o) => (
          <option key={o.code} value={o.code}>
            {o.label}
          </option>
        ))}
      </select>
      <Btn
        disabled={upload.isPending}
        onClick={() => {
          onArm();
          input.current?.click();
        }}
      >
        <Upload size={13} />
        Выбрать файлы
      </Btn>
      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          send([...(e.target.files ?? [])]);
          e.target.value = "";
        }}
      />
      <Hint className="min-w-[200px] flex-1">
        перетащите сюда или нажмите Ctrl-V{armed ? "" : " (после щелчка по этой панели)"} — первый файл встанет в слот,
        остальные лягут в библиотеку проекта
      </Hint>
      {status ? (
        <span className={clsx("text-[12px]", upload.isError ? "text-err" : "text-fg-muted")}>{status}</span>
      ) : null}
    </div>
  );
}
